import type { Amount, Mall, PlBlock, PlResponse } from "../../../types/pl";
import { weekday } from "../../../lib/plChart";
import { BasisBadge } from "../../../components/BasisBadge";
import { num, pct, profitColor, roasFmt, yen } from "../../../lib/plFormat";

const MALLS: Mall[] = ["shopify", "rakuten", "amazon"];
const MALL_LABEL: Record<Mall, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
const MALL_COLOR: Record<Mall, string> = { shopify: "#5a8f4e", rakuten: "#c43d3a", amazon: "#e0922a" };

interface Row {
  label: string;
  bold?: boolean;
  subtotalBg?: boolean;
  totalBg?: boolean;
  indent?: boolean;
  neg?: boolean;
  cells: { text: string; pctText: string; color?: string; badge?: React.ReactNode }[];
}

function amountRow(
  label: string,
  cols: PlBlock[],
  pick: (b: PlBlock) => Amount,
  opts: { neg?: boolean; indent?: boolean } = {}
): Row {
  return {
    label,
    neg: opts.neg,
    indent: opts.indent,
    cells: cols.map((b) => {
      const a = pick(b);
      return {
        text: (opts.neg ? "−" : "") + yen(a.value),
        pctText: pct(a.value / (b.sales.value || 1)),
        badge: <BasisBadge basis={a.basis} note={a.note} />,
      };
    }),
  };
}

export function MallTab({ data }: { data: PlResponse }) {
  const { total, malls, daily, meta } = data;
  const cols: PlBlock[] = [total, malls.shopify, malls.rakuten, malls.amazon];

  const rows: Row[] = [
    { label: "売上高", bold: true, cells: cols.map((b) => ({ text: yen(b.sales.value), pctText: "100%" })) },
    amountRow("商品原価", cols, (b) => b.cogs, { neg: true, indent: true }),
    {
      label: "粗利",
      bold: true,
      subtotalBg: true,
      cells: cols.map((b) => ({ text: yen(b.gross_profit), pctText: pct(b.gross_profit / (b.sales.value || 1)) })),
    },
    amountRow("モール手数料", cols, (b) => b.mall_fee, { neg: true, indent: true }),
    amountRow("決済手数料", cols, (b) => b.payment_fee, { neg: true, indent: true }),
    amountRow("送料・物流費", cols, (b) => b.shipping, { neg: true, indent: true }),
    amountRow("ポイント・クーポン原資", cols, (b) => b.points_coupon, { neg: true, indent: true }),
    amountRow("広告費", cols, (b) => b.ad_spend, { neg: true, indent: true }),
    {
      label: "貢献利益",
      bold: true,
      totalBg: true,
      cells: cols.map((b) => ({
        text: yen(b.contribution),
        pctText: pct(b.contribution / (b.sales.value || 1)),
        color: profitColor(b.contribution),
      })),
    },
    {
      label: "注文数 / 客単価",
      cells: cols.map((b) => ({ text: `${num(b.orders)} 件 ・ ${yen(b.aov ?? 0)}`, pctText: "" })),
    },
    {
      label: "ROAS",
      cells: cols.map((b) => ({ text: roasFmt(b.roas), pctText: "" })),
    },
  ];

  const dailyDates = Object.keys(
    daily.reduce<Record<string, true>>((acc, d) => {
      acc[d.date] = true;
      return acc;
    }, {})
  ).sort((a, b) => (a < b ? 1 : -1)); // 新しい日付順

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="pl-card">
        <span className="pl-card-title">モール別 損益計算書</span>
        <div className="pl-table-wrap">
          <div className="pl-table" style={{ minWidth: 760 }}>
            <div className="pl-table-head-row" style={{ gridTemplateColumns: "minmax(160px,1.4fr) repeat(4,minmax(140px,1fr))" }}>
              <span>勘定科目</span>
              <span className="pl-table-cell-num" style={{ justifyContent: "flex-end" }}>
                合計
              </span>
              {MALLS.map((m) => (
                <span key={m} className="pl-table-cell-num" style={{ justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: MALL_COLOR[m], display: "inline-block" }} />
                  {MALL_LABEL[m]}
                </span>
              ))}
            </div>
            {rows.map((r, i) => (
              <div
                key={i}
                className={`pl-table-row ${r.subtotalBg ? "pl-row-subtotal" : ""} ${r.totalBg ? "pl-row-total" : ""}`}
                style={{ gridTemplateColumns: "minmax(160px,1.4fr) repeat(4,minmax(140px,1fr))", fontWeight: r.bold ? (r.totalBg ? 700 : 600) : 400 }}
              >
                <span className={r.indent ? "pl-row-indent" : ""}>{r.label}</span>
                {r.cells.map((c, ci) => (
                  <span key={ci} className="pl-table-cell-num" style={{ color: c.color }}>
                    {c.text}
                    {c.badge}
                    {c.pctText && <span className="pl-table-cell-pct">{c.pctText}</span>}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="pl-card">
        <div className="pl-card-head">
          <span className="pl-card-title">日別 モール別売上・利益</span>
          <span className="pl-card-sub">新しい日付順</span>
        </div>
        <div className="pl-table-wrap pl-daily-table-scroll">
          <div className="pl-table" style={{ minWidth: 860 }}>
            <div className="pl-table-head-row pl-daily-head" style={{ gridTemplateColumns: "90px repeat(4,minmax(170px,1fr))" }}>
              <span>日付</span>
              <span className="pl-table-cell-num" style={{ justifyContent: "flex-end" }}>
                合計 売上 / 利益
              </span>
              {MALLS.map((m) => (
                <span key={m} className="pl-table-cell-num" style={{ justifyContent: "flex-end" }}>
                  {MALL_LABEL[m]}
                </span>
              ))}
            </div>
            {dailyDates.map((date) => {
              const row = daily.find((d) => d.date === date)!;
              const dow = weekday(date);
              const dColor = dow === 0 ? "var(--pl-neg)" : dow === 6 ? "var(--pl-sat)" : "var(--pl-text)";
              const totalSales = MALLS.reduce((s, m) => s + (row.malls[m]?.sales ?? 0), 0);
              const totalProfit = MALLS.reduce((s, m) => s + (row.malls[m]?.contribution ?? 0), 0);
              return (
                <div key={date} className="pl-table-row" style={{ gridTemplateColumns: "90px repeat(4,minmax(170px,1fr))" }}>
                  <span style={{ color: dColor }}>{date.slice(5).replace("-", "/")}</span>
                  <span className="pl-table-cell-num" style={{ fontWeight: 600 }}>
                    {yen(totalSales)}
                    <span style={{ minWidth: 74, textAlign: "right", color: totalProfit < 0 ? "var(--pl-neg)" : "var(--pl-cost-profit)" }}>
                      {yen(totalProfit)}
                    </span>
                  </span>
                  {MALLS.map((m) => {
                    const pt = row.malls[m];
                    const p = pt?.contribution ?? 0;
                    return (
                      <span key={m} className="pl-table-cell-num">
                        {yen(pt?.sales ?? 0)}
                        <span style={{ minWidth: 74, textAlign: "right", color: p < 0 ? "var(--pl-neg)" : "var(--pl-cost-profit)" }}>
                          {yen(p)}
                        </span>
                      </span>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {meta.malls.some((m) => m.status === "not_connected") && (
        <p className="pl-footer-note">
          未連携モール（{meta.malls.filter((m) => m.status === "not_connected").map((m) => m.id).join("・")}）は0円・「未連携」表示です。
        </p>
      )}
    </div>
  );
}
